import React, { useRef, useEffect } from 'react';
// @ts-ignore
import { CommentItem } from '@lesmills-international/components';
import PostComment from '../postComment/PostComment';
import { isCommentSafeToDisplay, canUserDeleteComment } from '../../../utils/workoutCommentsHelpers';
import { CHARACTER_LIMITS, USER_DISPLAY } from '../../../constants/workout';
import COLORS from '../../../themes/colors';
import ShowMoreWrapper from '../../atoms/showMore/style';
import MultilineText from '../../atoms/multilineText';
import { convertToRelativeTime } from '../../../utils/utilities';
import { ReactComponent as ThreeDotsIcon } from '../../../assets/icons/three_dots.svg';
import { ReactComponent as ReportIcon } from '../../../assets/icons/report.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete.svg';
import {
  DropdownMenu,
  DropdownMenuOverlay,
  MenuItem,
  SpacerLeft,
  DeleteIconWrapper,
  OptionsWrapper,
  Spacer,
} from '../../../styles/workoutPageStyles';

interface CommentRepliesProps {
  comment: any;
  rtNewWorkoutComments: boolean;
  COMMENTS_MODERATION_FF: boolean;
  clickedCommentId: string;
  showThreeDotsMenu: boolean;
  showReplyInput: boolean;
  givenName: string | null;
  cancelText: string;
  inputPlaceholderText: string;
  clickedComment: any;
  vimeoUserId: string | null;
  prismicData: any;
  handleShowCommentMenu: (id: string) => void;
  handleOpenDeleteDialog: () => void;
  handleOpenReportDialog: () => void;
  handleWorkoutReply: (comment: string, parentComment: any) => void;
  handleReply: (comment: string, parentId: string) => void;
  handleCancelReply: () => void;
}

// 🎯 OPTIMIZATION: Memoized CommentReplies component for better performance
const CommentReplies: React.FC<CommentRepliesProps> = React.memo(({
  comment,
  rtNewWorkoutComments,
  COMMENTS_MODERATION_FF,
  clickedCommentId,
  showThreeDotsMenu,
  showReplyInput,
  givenName,
  cancelText,
  inputPlaceholderText,
  clickedComment,
  vimeoUserId,
  prismicData,
  handleShowCommentMenu,
  handleOpenDeleteDialog,
  handleOpenReportDialog,
  handleWorkoutReply,
  handleReply,
  handleCancelReply,
}) => {
  const hasReplies = (rtNewWorkoutComments && comment.repliedComments && comment.repliedComments.length > 0) ||
                    (!rtNewWorkoutComments && comment.isReplied);

  const shouldShowReplyInput = clickedCommentId === comment.id && showReplyInput;
  
  // 🎯 OPTIMIZATION: Auto-focus reply input when opened (NEW API only)
  const replyInputRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (rtNewWorkoutComments && shouldShowReplyInput && replyInputRef.current) {
      // Find and focus the textarea/input inside the PostComment component
      const inputElement = replyInputRef.current.querySelector('textarea, input') as HTMLElement;
      if (inputElement) {
        setTimeout(() => inputElement.focus(), 100); // Small delay for smooth UX
      }
    }
  }, [rtNewWorkoutComments, shouldShowReplyInput]);

  // Show component if there are existing replies OR if reply input should be shown
  if (!hasReplies && !shouldShowReplyInput) return null;

  return (
    <>
      {/* Display replies: New API uses repliedComments array, Legacy API uses isReplied flag */}
      {hasReplies && comment.repliedComments
        ?.filter((reply: any) =>
          rtNewWorkoutComments ? isCommentSafeToDisplay(reply) : true
        )
        ?.map((reply: any) => (
          <div
            key={reply.id}
            style={{
              marginLeft: 'clamp(20px, 5vw, 40px)', // Responsive reply indentation
              position: 'relative',
              marginBottom: 'clamp(6px, 1.5vw, 12px)', // Responsive spacing
            }}
          >
            {rtNewWorkoutComments ? (
              // NEW API: Custom layout with three-dots inline
              <>
                {/* Header with user, timestamp, and three-dots close together */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '4px',
                  fontSize: '14px',
                  color: COLORS.lightGrey7B // Design system light grey
                }}>
                  <strong style={{ color: COLORS.white, marginRight: '8px' }}>
                    {reply.user.name ? reply.user.name : 'Anonymous'}
                  </strong>
                  <span style={{ marginRight: '4px' }}>{convertToRelativeTime(reply.createdAt)}</span>
                  
                  {/* Three-dots positioned right after timestamp - much closer */}
                  {COMMENTS_MODERATION_FF && canUserDeleteComment(reply, vimeoUserId) && (
                    <div style={{ position: 'relative', display: 'inline-block' }} className="dropdown-trigger"> {/* Positioning context for NEW API overlay */}
                      <ShowMoreWrapper
                        hoverState
                        onClick={() => {
                          if (reply?.id) {
                            handleShowCommentMenu(reply.id);
                          }
                        }}
                        style={{
                          padding: 'clamp(4px, 1.5vw, 8px)',
                          borderRadius: 'clamp(3px, 1vw, 6px)',
                          backgroundColor: 'transparent',
                          // 🎯 OPTIMIZATION: Touch-friendly delete button on mobile
                          minWidth: 'clamp(32px, 8vw, 44px)', // iOS recommended 44px touch target
                          minHeight: 'clamp(32px, 8vw, 44px)',
                          opacity: 0.6,
                          transition: 'opacity 0.2s ease',
                          marginLeft: '4px', // Minimal gap after timestamp
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '0.6';
                        }}
                      >
                        <ThreeDotsIcon />
                      </ShowMoreWrapper>
                      
                      {/* NEW API: Overlay dropdown positioned relative to this button */}
                      {rtNewWorkoutComments && clickedCommentId === reply.id && showThreeDotsMenu && (
                        <DropdownMenuOverlay data-dropdown-overlay>
                          {/* Security: Only show delete button if user is comment author */}
                          {canUserDeleteComment(reply, vimeoUserId) && (
                            <MenuItem onClick={handleOpenDeleteDialog}>
                              <DeleteIconWrapper>
                                <DeleteIcon />
                              </DeleteIconWrapper>
                              Delete
                            </MenuItem>
                          )}
                          {/* No report button for NEW API per security requirements */}
                        </DropdownMenuOverlay>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Comment content - multiline support + responsive word wrapping for NEW API */}
                <MultilineText enableMultiline={rtNewWorkoutComments}>
                  <div style={{ 
                    color: COLORS.white, // Design system white
                    fontSize: 'clamp(14px, 4vw, 16px)', // Responsive font size
                    lineHeight: 'clamp(1.2, 2vw, 1.5)', // Responsive line height
                    marginBottom: 'clamp(6px, 2vw, 8px)', // Responsive spacing
                    // Additional word-breaking protection for nested content - RESPONSIVE
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    maxWidth: '100%',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}>
                    {reply.content}
                  </div>
                </MultilineText>
              </>
            ) : (
              // LEGACY API: Original CommentItem + OptionsWrapper (word wrapping only, no multiline)
              <>
                <MultilineText enableMultiline={false}>
                  <CommentItem
                    author={reply.user?.name || USER_DISPLAY.ANONYMIZED_USER_NAME}
                    content={reply.content}
                    timestamp={convertToRelativeTime(reply.createdAt)}
                  />
                </MultilineText>
                <OptionsWrapper>
                  {COMMENTS_MODERATION_FF && (
                    <ShowMoreWrapper
                      hoverState
                      onClick={() => handleShowCommentMenu(reply?.id)}
                    >
                      <ThreeDotsIcon />
                    </ShowMoreWrapper>
                  )}
                </OptionsWrapper>
                <Spacer />
              </>
            )}

            {/* LEGACY API: Original inline dropdown (only for legacy, NEW API uses overlay above) */}
            {!rtNewWorkoutComments && clickedCommentId === reply.id && showThreeDotsMenu && (
              <DropdownMenu>
                {/* Show delete button for all users (legacy behavior) */}
                <MenuItem onClick={handleOpenDeleteDialog}>
                  <DeleteIconWrapper>
                    <DeleteIcon />
                  </DeleteIconWrapper>
                  Delete
                </MenuItem>
                {/* Report button kept for legacy API */}
                <MenuItem onClick={handleOpenReportDialog}>
                  <ReportIcon />
                  <SpacerLeft />
                  Report
                </MenuItem>
              </DropdownMenu>
            )}
          </div>
        ))}
      
      {/* Reply input for main comment - aligned with replies */}
      {shouldShowReplyInput && (
        <div 
          ref={replyInputRef} // 🎯 OPTIMIZATION: Ref for auto-focus
          style={{ 
            marginLeft: 'clamp(20px, 5vw, 40px)', // Responsive reply input indentation (matches replies)
            marginTop: 'clamp(8px, 2vw, 16px)', // Responsive top spacing
          }}
        >
          <PostComment
            author={givenName || ''}
            onPost={(reply: string) => {
              if (rtNewWorkoutComments) {
                handleWorkoutReply(reply, clickedComment);
              } else {
                handleReply(reply, clickedCommentId);
              }
            }}
            onCancel={handleCancelReply}
            cancelText={cancelText}
            postCommentText="Reply"
            inputPlaceholderText={inputPlaceholderText}
            validateSpaces={rtNewWorkoutComments} // Enhanced validation only for NEW API
            showCharacterCounter={rtNewWorkoutComments} // Character counter only for NEW API
            maxCharacters={CHARACTER_LIMITS.MAX_LENGTH} // Backend limit from constants
            allowNewLines={rtNewWorkoutComments} // Enable multiline for NEW API
            prismicData={prismicData} // Pass Prismic data for i18n support
          />
        </div>
      )}
    </>
  );
});

export default CommentReplies;
